import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  LevelFormat,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  type IParagraphOptions,
  type ParagraphChild,
} from "docx";
import type { AnalysisDocument } from "@/api/analysis-api";

/**
 * The editor's HTML, as a Word document.
 *
 * Written by hand rather than pulled from a converter library because the ones
 * that take HTML directly (html-to-docx and friends) are Node-only — Buffer,
 * jsdom — and this has to run in the browser. The mapping is small precisely
 * because images are out of scope: text, marks, lists and tables is all of it.
 *
 * Anything the mapper does not recognise falls through to a plain paragraph
 * rather than disappearing. Losing a heading's size is a blemish; losing its
 * text is a bug.
 */

/** Heading depth is shifted: section titles take Heading 1 in the export. */
const HEADING_FOR_TAG: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  H1: HeadingLevel.HEADING_2,
  H2: HeadingLevel.HEADING_3,
  H3: HeadingLevel.HEADING_4,
  H4: HeadingLevel.HEADING_5,
  H5: HeadingLevel.HEADING_6,
  H6: HeadingLevel.HEADING_6,
};

const ORDERED_LIST_REFERENCE = "analysis-ordered";

interface InlineFormat {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  strike?: boolean;
  code?: boolean;
  link?: string;
}

/** Marks carried down the tree, since <strong><em>x</em></strong> nests. */
const formatFor = (element: Element, inherited: InlineFormat): InlineFormat => {
  const tag = element.tagName;
  return {
    ...inherited,
    bold: inherited.bold || tag === "STRONG" || tag === "B",
    italics: inherited.italics || tag === "EM" || tag === "I",
    underline: inherited.underline || tag === "U",
    strike: inherited.strike || tag === "S" || tag === "STRIKE" || tag === "DEL",
    code: inherited.code || tag === "CODE",
    link:
      tag === "A"
        ? element.getAttribute("href") || inherited.link
        : inherited.link,
  };
};

const runFor = (text: string, format: InlineFormat): ParagraphChild => {
  // Only truthy marks are passed: docx writes an explicit `w:val="false"` for a
  // mark set to false, which clutters the file and says something it needn't.
  const run = new TextRun({
    text,
    ...(format.bold ? { bold: true } : {}),
    ...(format.italics ? { italics: true } : {}),
    ...(format.underline ? { underline: {} } : {}),
    ...(format.strike ? { strike: true } : {}),
    // Word has no inline-code style, so a monospace font carries it.
    ...(format.code ? { font: "Consolas", color: "9A3412" } : {}),
  });

  if (!format.link) return run;

  return new ExternalHyperlink({ children: [run], link: format.link });
};

/** Inline content of one block element. */
export const inlineChildren = (
  node: Node,
  inherited: InlineFormat = {}
): ParagraphChild[] => {
  const children: ParagraphChild[] = [];

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent ?? "";
      // Whitespace between block tags is layout, not content — but a space
      // between two inline runs is meaningful, so only fully blank runs go.
      if (text.trim() || (text && children.length)) {
        children.push(runFor(text, inherited));
      }
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const element = child as Element;

    if (element.tagName === "BR") {
      children.push(new TextRun({ break: 1 }));
      return;
    }

    children.push(...inlineChildren(element, formatFor(element, inherited)));
  });

  return children;
};

/**
 * One block element as a paragraph.
 *
 * `inherited` exists for cases where the mark comes from the container rather
 * than from a tag inside it — a `<th>` is bold because it is a header, and a
 * blockquote is italic because it is a quote.
 */
const paragraph = (
  node: Node,
  options: IParagraphOptions = {},
  inherited: InlineFormat = {}
): Paragraph => {
  const children = inlineChildren(node, inherited);
  return new Paragraph({
    ...options,
    // Last, deliberately: a `children` in `options` would otherwise be dropped
    // here without a word, which is how header cells quietly lost their bold.
    children: children.length ? children : [new TextRun("")],
  });
};

const cellsOf = (row: Element): TableCell[] =>
  Array.from(row.querySelectorAll(":scope > td, :scope > th")).map(
    (cell) =>
      new TableCell({
        children: [
          // A <th> is bold because of the tag it is, which no mark inside it
          // records.
          paragraph(cell, {}, cell.tagName === "TH" ? { bold: true } : {}),
        ],
        width: { size: 100, type: WidthType.AUTO },
      })
  );

const tableFor = (element: Element): Table | Paragraph => {
  const rows = Array.from(element.querySelectorAll("tr"));
  if (!rows.length) return paragraph(element);

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map(
      (row) =>
        new TableRow({
          children: cellsOf(row),
          tableHeader: row.closest("thead") !== null,
        })
    ),
  });
};

const listParagraphs = (
  element: Element,
  ordered: boolean,
  depth: number
): (Paragraph | Table)[] => {
  const blocks: (Paragraph | Table)[] = [];

  Array.from(element.children).forEach((item) => {
    if (item.tagName !== "LI") return;

    /*
     * Block content nested inside the <li> comes out first, or it gets
     * flattened into the bullet's own text.
     *
     * Not just nested lists: a table inside a list item is how this actually
     * happens in use — pressing Enter at the end of a bulleted section makes a
     * new list item, and inserting a table there puts it inside the <li>. That
     * turned a real table into a paragraph of run-together cell text.
     */
    const nested = Array.from(item.children).filter((child) =>
      NESTED_IN_LIST_ITEM.has(child.tagName)
    );
    nested.forEach((child) => child.remove());

    blocks.push(
      paragraph(
        item,
        ordered
          ? { numbering: { reference: ORDERED_LIST_REFERENCE, level: Math.min(depth, 2) } }
          : { bullet: { level: Math.min(depth, 2) } }
      )
    );

    nested.forEach((child) => {
      if (child.tagName === "UL" || child.tagName === "OL") {
        blocks.push(...listParagraphs(child, child.tagName === "OL", depth + 1));
        return;
      }
      blocks.push(...blockFor(child));
    });
  });

  return blocks;
};

/** Block-level tags that can legally sit inside an <li>. */
const NESTED_IN_LIST_ITEM = new Set([
  "UL", "OL", "TABLE", "BLOCKQUOTE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6",
]);

/** One block-level element from the editor. */
const blockFor = (element: Element): (Paragraph | Table)[] => {
  const tag = element.tagName;

  if (HEADING_FOR_TAG[tag]) {
    return [paragraph(element, { heading: HEADING_FOR_TAG[tag] })];
  }

  if (tag === "UL" || tag === "OL") {
    return listParagraphs(element, tag === "OL", 0);
  }

  if (tag === "TABLE") {
    return [tableFor(element)];
  }

  if (tag === "BLOCKQUOTE") {
    return [
      paragraph(
        element,
        {
          indent: { left: 360 },
          border: {
            left: { style: BorderStyle.SINGLE, size: 12, color: "D4D4D8", space: 8 },
          },
        },
        { italics: true }
      ),
    ];
  }

  if (tag === "HR") {
    return [
      new Paragraph({
        text: "",
        border: {
          bottom: { style: BorderStyle.SINGLE, size: 6, color: "D4D4D8", space: 1 },
        },
      }),
    ];
  }

  if (tag === "PRE") {
    return [paragraph(element, { shading: { fill: "F4F4F5" } }, { code: true })];
  }

  // Unknown wrapper: recurse if it holds blocks, otherwise treat as a paragraph.
  const childBlocks = Array.from(element.children).filter((child) =>
    BLOCK_TAGS.has(child.tagName)
  );
  if (childBlocks.length) {
    return childBlocks.flatMap(blockFor);
  }

  return [paragraph(element)];
};

const BLOCK_TAGS = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6",
  "UL", "OL", "TABLE", "BLOCKQUOTE", "HR", "PRE", "DIV",
]);

/** Every block in one section's stored HTML. */
export const htmlToDocxBlocks = (html: string): (Paragraph | Table)[] => {
  const trimmed = (html ?? "").trim();
  if (!trimmed) return [];

  const parsed = new DOMParser().parseFromString(trimmed, "text/html");

  return Array.from(parsed.body.children).flatMap((element) =>
    blockFor(element)
  );
};

export interface AnalysisDocxOptions {
  /** Shown under the title, e.g. the company the memo is about. */
  subtitle?: string;
}

export const buildAnalysisDocx = (
  analysis: AnalysisDocument,
  options: AnalysisDocxOptions = {}
): Document => {
  const children: (Paragraph | Table)[] = [
    new Paragraph({
      text: analysis.template_name,
      heading: HeadingLevel.TITLE,
    }),
  ];

  if (options.subtitle) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: options.subtitle, color: "71717A" })],
        alignment: AlignmentType.LEFT,
        spacing: { after: 240 },
      })
    );
  }

  analysis.sections.forEach((section) => {
    children.push(
      new Paragraph({
        text: section.title,
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 320, after: 120 },
      })
    );

    const blocks = htmlToDocxBlocks(section.html);

    if (blocks.length) {
      children.push(...blocks);
      return;
    }

    // An unfinished section is exported as a note rather than omitted: a memo
    // with a silently missing section reads as complete when it is not.
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text:
              section.status === "error"
                ? "This section failed to generate."
                : "This section has not been generated yet.",
            italics: true,
            color: "A1A1AA",
          }),
        ],
      })
    );
  });

  return new Document({
    numbering: {
      config: [
        {
          reference: ORDERED_LIST_REFERENCE,
          levels: [0, 1, 2].map((level) => ({
            level,
            format: LevelFormat.DECIMAL,
            text: `%${level + 1}.`,
            alignment: AlignmentType.START,
            style: { paragraph: { indent: { left: 360 * (level + 1), hanging: 260 } } },
          })),
        },
      ],
    },
    sections: [{ children }],
  });
};
